import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import { NestedOutageStatus } from '../../types/types';
import { RestorationStatusFAQ } from '../restoration-status-faq/RestorationStatusFAQ';
import { getRestorationStatus } from '../../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accordion: {
      '& .MuiAccordionSummary-root': {
        alignItems: 'start',
        padding: 0,
      },
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '& .MuiAccordionSummary-expandIcon': {
        marginTop: 5,
        marginRight: -32,
        right: 25,
      },
    },
    accordionDetails: {
      padding: 0,
      flexDirection: 'column',
    },
    accordionContainer: {
      padding: theme.spacing(3.75, 2.5),
    },
    accordionTriggerContent: {
      width: '100%',
    },
    noteBox: {
      backgroundColor: '#F5F5F5',
      borderRadius: 10,
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    fontBold: {
      fontWeight: 600,
    },
    statusLabel: {
      width: '55%',
    },
    statusValue: {
      width: '45%',
    },

    location: {
      fontSize: 16,
    },
    restorationStatus: {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: colors.sparkBlue,
    },
  }),
);

interface NestedOutageStatusAccordionProps {
  location?: string;
  nestedOutageStatus?: NestedOutageStatus;
  triggerContent: React.ReactNode;
}

export const NestedOutageStatusAccordion: React.FC<NestedOutageStatusAccordionProps> = ({
  location,
  nestedOutageStatus,
  triggerContent,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [
    openRestorationStatusFAQ,
    setOpenRestorationStatusFAQ,
  ] = React.useState(false);

  const { title, description } = getRestorationStatus(
    nestedOutageStatus?.restorationStatus || '',
  );

  return (
    <>
      <Box className={classes.accordionContainer}>
        <Accordion elevation={0} className={classes.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="large" />}>
            <Box
              display={'flex'}
              flexDirection={'column'}
              gridGap={20}
              className={classes.accordionTriggerContent}
            >
              {triggerContent}
              {location && (
                <Typography variant="body2" className={classes.location}>
                  {location}
                </Typography>
              )}
              <Box display={'flex'} flexDirection={'column'} gridGap={5}>
                <Box display={'flex'}>
                  <Typography variant="body2" className={classes.statusLabel}>
                    {t('ESTIMATED_RESTORATION_NEW')}:
                  </Typography>
                  <Typography
                    variant="body2"
                    className={clsx(classes.statusValue, classes.fontBold)}
                  >
                    {nestedOutageStatus?.estimatedRestoration}
                  </Typography>
                </Box>
                <Box display={'flex'}>
                  <Typography variant="body2" className={classes.statusLabel}>
                    {t('RESTORATION_STATUS_NEW')}:
                  </Typography>
                  <Typography
                    variant="body2"
                    className={clsx(
                      classes.statusValue,
                      classes.fontBold,
                      classes.restorationStatus,
                    )}
                    onClick={() => setOpenRestorationStatusFAQ(true)}
                  >
                    {title}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Box display={'flex'} flexDirection={'column'} gridGap={5} mt={0.7}>
              <Box display={'flex'}>
                <Typography variant="body2" className={classes.statusLabel}>
                  {t('CUSTOMERS_AFFECTED')}:
                </Typography>
                <Typography variant="body2" className={classes.statusValue}>
                  {nestedOutageStatus?.customersAffected}
                </Typography>
              </Box>
              <Box display={'flex'}>
                <Typography variant="body2" className={classes.statusLabel}>
                  {t('OUTAGE_IDENTIFIED')}:
                </Typography>
                <Typography variant="body2" className={classes.statusValue}>
                  {nestedOutageStatus?.outageIdentified}
                </Typography>
              </Box>
              {nestedOutageStatus?.causeOfOutage &&
                nestedOutageStatus?.causeOfOutage !== 'Under Investigation' && (
                  <Box display={'flex'}>
                    <Typography variant="body2" className={classes.statusLabel}>
                      {t('CAUSE_OF_OUTAGE')}:
                    </Typography>
                    <Typography variant="body2" className={classes.statusValue}>
                      {nestedOutageStatus?.causeOfOutage}
                    </Typography>
                  </Box>
                )}
            </Box>
            {description && (
              <Box className={classes.noteBox}>
                <Typography variant="body2">{description}</Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
      <RestorationStatusFAQ
        open={openRestorationStatusFAQ}
        setOpen={setOpenRestorationStatusFAQ}
      />
    </>
  );
};
